import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { makeStyles } from '@mui/styles';
import moment from 'moment';
import 'moment/locale/pt-br';

// How far ahead a notification can be scheduled.
export const MAX_SCHEDULE_DAYS = 30;

const useStyles = makeStyles(() => ({
    label: {
        display: 'block',
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: 1.8,
        color: '#000',
    },
}));

// Formats a Date into the "YYYY-MM-DDTHH:mm" string used as the field value,
// keeping the user's local timezone.
const toLocalInputValue = (date) => {
    const d = new Date(date);
    const localMs = d.getTime() - (d.getTimezoneOffset() * 60000);
    return new Date(localMs).toISOString().slice(0, 16);
};

/**
 * Date/time picker used to schedule a push notification.
 *
 * Keeps a plain "YYYY-MM-DDTHH:mm" string as its value so the parent state and
 * API payload stay timezone-stable; all moment conversion happens here.
 */
export default function ScheduleDateTimePicker({ value, onChange }) {
    const classes = useStyles();
    const [focused, setFocused] = useState(false);

    const minDateTime = toLocalInputValue(new Date(Date.now() + 60 * 1000));
    const maxDateTime = toLocalInputValue(new Date(Date.now() + MAX_SCHEDULE_DAYS * 24 * 60 * 60 * 1000));

    return (
        <>
            <label className={classes.label}>Data e horário de envio</label>
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale="pt-br"
                localeText={{
                    cancelButtonLabel: 'CANCELAR',
                    okButtonLabel: 'CONFIRMAR',
                    fieldDayPlaceholder: () => 'dd',
                    fieldMonthPlaceholder: () => 'mm',
                    fieldYearPlaceholder: () => 'aaaa',
                    fieldHoursPlaceholder: () => 'hh',
                    fieldMinutesPlaceholder: () => 'mm',
                }}
            >
                <Box sx={{ position: 'relative' }}>
                    <DateTimePicker
                        value={value ? moment(value) : null}
                        onChange={(v) => onChange(v && v.isValid() ? v.format('YYYY-MM-DDTHH:mm') : '')}
                        format="DD/MM/YYYY [às] HH:mm"
                        ampm={false}
                        timeSteps={{ hours: 1, minutes: 1 }}
                        minDateTime={moment(minDateTime)}
                        maxDateTime={moment(maxDateTime)}
                        slotProps={{
                            textField: {
                                placeholder: 'dd/mm/aaaa às hh:mm',
                                fullWidth: true,
                                onFocus: () => setFocused(true),
                                onBlur: () => setFocused(false),
                            },
                            actionBar: { actions: ['cancel', 'accept'] },
                            desktopPaper: {
                                sx: {
                                    // Calendar header: month centered, arrows on each corner
                                    '& .MuiPickersCalendarHeader-root': {
                                        position: 'relative',
                                        justifyContent: 'center',
                                    },
                                    '& .MuiPickersCalendarHeader-labelContainer': {
                                        margin: '0 auto',
                                    },
                                    '& .MuiPickersArrowSwitcher-root': {
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        justifyContent: 'space-between',
                                        pointerEvents: 'none',
                                    },
                                    '& .MuiPickersArrowSwitcher-button': { pointerEvents: 'auto' },
                                    // "Horário" header above the time columns
                                    '& .MuiMultiSectionDigitalClock-root': {
                                        position: 'relative',
                                        paddingTop: '40px',
                                        '&::before': {
                                            content: '"Horário"',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            padding: '16px 0 0 20px',
                                            fontSize: '1rem',
                                            fontWeight: 500,
                                            color: 'rgba(0,0,0,0.9)',
                                        },
                                    },
                                    '& .MuiMultiSectionDigitalClockSection-root': {
                                        scrollbarWidth: 'thin',
                                        width: 'auto',
                                        paddingTop: '15px',
                                        overflowY: 'scroll',
                                        '&::after': { display: 'none' },
                                    },
                                    '& .MuiMultiSectionDigitalClockSection-item': {
                                        borderRadius: '8px',
                                        width: '80px',
                                        margin: '0 20px',
                                    },
                                },
                            },
                        }}
                    />
                    {!value && !focused &&
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '1px',
                                bottom: '1px',
                                left: '1px',
                                right: '44px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                pl: '14px',
                                borderRadius: '4px',
                                bgcolor: 'background.paper',
                                color: 'text.secondary',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                pointerEvents: 'none',
                            }}
                        >
                            Selecione a data e o horário de envio
                        </Box>
                    }
                </Box>
            </LocalizationProvider>
            <Typography variant="caption">
                O envio será realizado automaticamente na data e horário selecionados. O agendamento pode ser feito com até {MAX_SCHEDULE_DAYS} dias de antecedência.
            </Typography>
        </>
    );
}
